/**
 * ConnectionStatusToast — surfaces the realtime connection state.
 *
 * Listens to the `status` value returned by `useUserConnection` and
 * shows a single toast that reflects the current state:
 *
 *   - `connected` → no toast (or dismisses the previous one and shows
 *     a brief success toast if we were just reconnecting)
 *   - `reconnecting` → persistent info toast while the backoff retries
 *   - `offline` → persistent warning toast until the browser comes
 *     back online
 *
 * One stable toast id (`realtime-status`) is reused for the
 * `reconnecting`/`offline` states so successive status flips replace
 * the toast in place instead of stacking. The success toast uses a
 * separate id with a short auto-dismiss.
 *
 * The initial transition `connecting → connected` is silent — the
 * realtime connection coming up on a fresh page load shouldn't toast.
 */

import { useEffect, useRef } from "react";

import { t } from "../i18n";
import type { ConnectionStatus } from "../lib/use-user-connection";
import { useToast } from "./ui";

const STATUS_TOAST_ID = "realtime-status";
const RECONNECTED_TOAST_ID = "realtime-reconnected";

interface ConnectionStatusToastProps {
  status: ConnectionStatus;
}

export function ConnectionStatusToast({ status }: ConnectionStatusToastProps): null {
  const { show, dismiss } = useToast();
  const prevStatusRef = useRef<ConnectionStatus>(status);

  useEffect(() => {
    const prev = prevStatusRef.current;
    if (prev === status) return;
    prevStatusRef.current = status;

    switch (status) {
      case "reconnecting":
        // We lost the live connection mid-session. The status will flip
        // back to `connected` once the backoff timer succeeds.
        show({
          id: STATUS_TOAST_ID,
          tone: "warning",
          title: t.connection.reconnectingTitle,
          description: t.connection.reconnectingDescription,
          duration: null,
          dismissLabel: t.connection.reconnectingDismissLabel,
        });
        break;

      case "offline":
        // navigator.onLine flipped to false, or the server revoked the
        // session (code 4001). Either way, no point retrying until the
        // browser tells us we're back.
        show({
          id: STATUS_TOAST_ID,
          tone: "warning",
          title: t.connection.offlineTitle,
          description: t.connection.offlineDescription,
          duration: null,
          dismissLabel: t.connection.offlineDismissLabel,
        });
        break;

      case "connected":
        // Only celebrate when we're recovering. The very first
        // `connecting → connected` on page load stays silent.
        dismiss(STATUS_TOAST_ID);
        if (prev === "reconnecting" || prev === "offline") {
          show({
            id: RECONNECTED_TOAST_ID,
            tone: "success",
            title: t.connection.reconnectedTitle,
            description: t.connection.reconnectedDescription,
            duration: 3_000,
            dismissLabel: t.connection.reconnectedDismissLabel,
          });
        }
        break;

      case "connecting":
        // Transient state on (re)connect. No UI noise — if it takes
        // long, `connecting → reconnecting` will toast on its own.
        break;
    }
  }, [status, show, dismiss]);

  return null;
}
